'use client';

import { useEffect } from 'react';
import { PLANS } from '@/libs/content/plans';
import { CASES } from '@/libs/content/casos';

// Expone herramientas del sitio a agentes vía la WebMCP API (navigator.modelContext).
// API experimental (Chrome EPP) — feature-gated: no hace nada si no está disponible.
// Planes y casos se derivan de la fuente única (src/libs/content/*); METODOLOGIA y
// CONTACTO son prosa estática (sin fuente estructurada — espejo de worker-mcp/src/data.js).

const planLine = (p) =>
  p.priceUSD === 'A medida'
    ? `- ${p.name}: a medida ${p.period}.`
    : `- ${p.name}: ${p.price} CLP / ${p.priceUSD} ${p.period}.`;

const PLANES =
  'Planes de Bison Agents:\n' +
  PLANS.map(planLine).join('\n') +
  '\nDetalle: https://bisonagents.com/planes';

const CASOS =
  'Casos de éxito (Score AEO 0–100):\n' +
  CASES.map((c) => `- ${c.name} (${c.cat}): ${c.score.from} → ${c.score.to} en ${c.months} meses.`).join('\n') +
  '\nDetalle: https://bisonagents.com/casos';

const METODOLOGIA =
  'Metodología AEO de Bison: ciclo trimestral D·A·O·P (Diagnosticar, Auditar, Optimizar, Posicionar). El Score AEO (0–100) mide visibilidad a partir de 4 componentes: menciones, citabilidad, posición y sentimiento; el share-of-voice se reporta aparte. El Diagnóstico 4D descompone las brechas en identidad, reputación, editorial y social (0–25 c/u). Primer diagnóstico en 72 horas.';

const CONTACTO =
  'Solicita un diagnóstico AEO (0–100 en 72 horas) en https://bisonagents.com/contacto · Planes: https://bisonagents.com/planes';

const TOOLS = [
  {
    name: 'bison_consultar_planes',
    description: 'Planes y precios de Bison Agents (CLP/USD), prompts y motores.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({ content: [{ type: 'text', text: PLANES }] }),
  },
  {
    name: 'bison_listar_casos',
    description: 'Casos de éxito de Bison Agents con su delta de Score AEO (0–100).',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({ content: [{ type: 'text', text: CASOS }] }),
  },
  {
    name: 'bison_resumen_metodologia',
    description: 'Metodología AEO de Bison: ciclo D·A·O·P, Score AEO y Diagnóstico 4D.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({ content: [{ type: 'text', text: METODOLOGIA }] }),
  },
  {
    name: 'bison_como_empezar',
    description: 'Cómo solicitar un diagnóstico AEO con Bison Agents y dónde contactar.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({ content: [{ type: 'text', text: CONTACTO }] }),
  },
];

// La API de WebMCP cambió (borrador del W3C Web ML CG): el getter pasó de
// navigator.modelContext a document.modelContext (Chrome 150 deprecó el alias; se
// retiró en Chrome 153 Dev) y provideContext() salió de la spec en favor de
// registerTool(tool, { signal }). Soportamos la API nueva y caemos a la vieja solo
// si el navegador aún no la tiene.
function registerTools(signal) {
  const mc =
    typeof document !== 'undefined' && document.modelContext
      ? document.modelContext
      : typeof navigator !== 'undefined'
        ? navigator.modelContext
        : undefined;
  if (!mc) return false;

  if (typeof mc.registerTool === 'function') {
    TOOLS.forEach((tool) => mc.registerTool(tool, { signal }));
    return true;
  }
  if (typeof mc.provideContext === 'function') {
    mc.provideContext({ tools: TOOLS });
    return true;
  }
  return false;
}

export default function WebMCP() {
  useEffect(() => {
    // El AbortController desregistra las tools al desmontar (y evita duplicados con
    // el doble efecto de React en desarrollo).
    const controller = new AbortController();
    try {
      registerTools(controller.signal);
    } catch {
      // API en evolución (feature-gated): fallar en silencio, pero nunca romper la página.
    }
    return () => controller.abort();
  }, []);

  return null;
}
